/**
 * ManageDisabledClausesDialog
 *
 * Modal that lists clauses fully disabled in a topic (all section types toggled
 * off). Users select which clauses and section types to re-enable. Opens from
 * the "Manage" button on a topic group header in DraftClausesTable.
 */

import {
    IDraftTopicDetail,
    IEnabledClauseSelection,
    ITopicClauseInfo,
} from "@/api/clause-checklists/checklist-generation.types";
import { Button } from "@/components/ui/button";
import { Checkbox } from "@/components/ui/checkbox";
import {
    Table,
    TableBody,
    TableCell,
    TableHead,
    TableHeader,
    TableRow,
} from "@/components/ui/table";
import { Modal } from "@/components/shared/Modal";
import { useEffect, useMemo, useState } from "react";

// ---------------------------------------------------------------------------
// Types
// ---------------------------------------------------------------------------

interface ManageDisabledClausesDialogProps {
    open: boolean;
    onClose: () => void;
    topic: IDraftTopicDetail;
    onEnable: (selections: IEnabledClauseSelection[]) => void;
}

// ---------------------------------------------------------------------------
// Component
// ---------------------------------------------------------------------------

export function ManageDisabledClausesDialog({
    open,
    onClose,
    topic,
    onEnable,
}: ManageDisabledClausesDialogProps) {
    const disabledClauses = useMemo(
        () =>
            topic.all_clauses.filter(
                (clause) =>
                    !topic.enabled_clauses.some(
                        (ec) => ec.topic_clause_id === clause.topic_clause_id
                    )
            ),
        [topic]
    );

    // Map<topicClauseId, Set<sectionTypeId | null>>
    const [selectedSections, setSelectedSections] = useState<
        Map<number, Set<number | null>>
    >(new Map());

    // Initialize: all section types checked by default for each disabled clause
    useEffect(() => {
        const initial = new Map<number, Set<number | null>>();
        for (const clause of disabledClauses) {
            const sectionIds = new Set<number | null>(
                clause.document_section_types.length > 0
                    ? clause.document_section_types.map((dst) => dst.id)
                    : [null]
            );
            initial.set(clause.topic_clause_id, sectionIds);
        }
        setSelectedSections(initial);
    }, [disabledClauses]);

    // -- Helpers -------------------------------------------------------------

    function isClauseFullySelected(clause: ITopicClauseInfo): boolean {
        const selected = selectedSections.get(clause.topic_clause_id);
        if (!selected) return false;
        const total =
            clause.document_section_types.length > 0
                ? clause.document_section_types.length
                : 1;
        return selected.size === total;
    }

    function isClausePartiallySelected(clause: ITopicClauseInfo): boolean {
        const selected = selectedSections.get(clause.topic_clause_id);
        if (!selected || selected.size === 0) return false;
        return !isClauseFullySelected(clause);
    }

    function toggleClause(clause: ITopicClauseInfo) {
        const totalSections =
            clause.document_section_types.length > 0
                ? clause.document_section_types.length
                : 1;

        setSelectedSections((prev) => {
            const next = new Map(prev);
            const current = prev.get(clause.topic_clause_id);
            const isFullySelected =
                current !== undefined && current.size === totalSections;

            if (isFullySelected) {
                next.set(clause.topic_clause_id, new Set());
            } else {
                const allIds = new Set<number | null>(
                    clause.document_section_types.length > 0
                        ? clause.document_section_types.map((dst) => dst.id)
                        : [null]
                );
                next.set(clause.topic_clause_id, allIds);
            }
            return next;
        });
    }

    function toggleSectionType(
        topicClauseId: number,
        sectionTypeId: number | null
    ) {
        setSelectedSections((prev) => {
            const next = new Map(prev);
            const current = new Set(next.get(topicClauseId) ?? []);
            if (current.has(sectionTypeId)) {
                current.delete(sectionTypeId);
            } else {
                current.add(sectionTypeId);
            }
            next.set(topicClauseId, current);
            return next;
        });
    }

    function collectSelections(
        clauseFilter?: (c: ITopicClauseInfo) => boolean
    ): IEnabledClauseSelection[] {
        const selections: IEnabledClauseSelection[] = [];
        for (const clause of disabledClauses) {
            if (clauseFilter && !clauseFilter(clause)) continue;
            const sectionIds = selectedSections.get(clause.topic_clause_id);
            if (!sectionIds || sectionIds.size === 0) continue;
            for (const sectionId of sectionIds) {
                selections.push({
                    topic_clause_id: clause.topic_clause_id,
                    document_section_type_id: sectionId,
                });
            }
        }
        return selections;
    }

    function handleEnableRow(clause: ITopicClauseInfo) {
        const selections = collectSelections(
            (c) => c.topic_clause_id === clause.topic_clause_id
        );
        if (selections.length > 0) onEnable(selections);
    }

    function handleEnableAll() {
        const selections = collectSelections();
        if (selections.length > 0) {
            onEnable(selections);
            onClose();
        }
    }

    const totalSelected = Array.from(selectedSections.values()).reduce(
        (sum, set) => sum + set.size,
        0
    );

    // -- Render --------------------------------------------------------------

    return (
        <Modal
            open={open}
            onClose={onClose}
            title={`Manage Disabled Clauses: ${topic.topic_name}`}
            maxWidth="md"
            actions={[
                {
                    label: `Enable Selected (${totalSelected})`,
                    onClick: handleEnableAll,
                    variant: "contained",
                    disabled: totalSelected === 0,
                },
            ]}
            showCancel
        >
            {disabledClauses.length === 0 ? (
                <p className="text-muted-foreground py-6 text-center text-sm">
                    All clauses in this topic are already enabled.
                </p>
            ) : (
                <Table>
                    <TableHeader>
                        <TableRow>
                            <TableHead className="w-9" />
                            <TableHead>Clause</TableHead>
                            <TableHead className="w-[220px]">
                                Section Types
                            </TableHead>
                            <TableHead className="w-[80px]" />
                        </TableRow>
                    </TableHeader>
                    <TableBody>
                        {disabledClauses.map((clause) => {
                            const sectionTypes =
                                clause.document_section_types.length > 0
                                    ? clause.document_section_types
                                    : [
                                          {
                                              id: null as number | null,
                                              name: "General",
                                          },
                                      ];
                            const selected = selectedSections.get(
                                clause.topic_clause_id
                            );

                            return (
                                <TableRow key={clause.topic_clause_id}>
                                    {/* Row checkbox */}
                                    <TableCell className="w-9 pt-3 pr-0 align-top">
                                        <Checkbox
                                            checked={isClauseFullySelected(
                                                clause
                                            )}
                                            indeterminate={isClausePartiallySelected(
                                                clause
                                            )}
                                            onCheckedChange={() =>
                                                toggleClause(clause)
                                            }
                                            aria-label={`Select ${clause.title}`}
                                        />
                                    </TableCell>

                                    {/* Clause info */}
                                    <TableCell className="whitespace-normal">
                                        <p className="text-[13px] leading-tight font-semibold">
                                            {clause.title}
                                        </p>
                                        {clause.description && (
                                            <p className="text-muted-foreground mt-0.5 line-clamp-2 text-[11px] leading-snug">
                                                {clause.description}
                                            </p>
                                        )}
                                    </TableCell>

                                    {/* Section type checkboxes */}
                                    <TableCell className="w-[220px] whitespace-normal">
                                        <div className="flex flex-col gap-1.5">
                                            {sectionTypes.map((st) => (
                                                <label
                                                    key={st.id ?? "general"}
                                                    className="flex cursor-pointer items-center gap-1.5 text-[12px]"
                                                >
                                                    <Checkbox
                                                        checked={
                                                            selected?.has(
                                                                st.id
                                                            ) ?? false
                                                        }
                                                        onCheckedChange={() =>
                                                            toggleSectionType(
                                                                clause.topic_clause_id,
                                                                st.id
                                                            )
                                                        }
                                                    />
                                                    <span>{st.name}</span>
                                                </label>
                                            ))}
                                        </div>
                                    </TableCell>

                                    {/* Per-row enable */}
                                    <TableCell className="w-[80px]">
                                        <Button
                                            variant="outline"
                                            size="xs"
                                            onClick={() =>
                                                handleEnableRow(clause)
                                            }
                                            disabled={
                                                !selected || selected.size === 0
                                            }
                                        >
                                            Enable
                                        </Button>
                                    </TableCell>
                                </TableRow>
                            );
                        })}
                    </TableBody>
                </Table>
            )}
        </Modal>
    );
}
